/**
 * Historique local des conversations de l'assistant IA des éditeurs
 * (tableur, traitement de texte, présentations). Stocké par document afin de
 * pouvoir reprendre une conversation plus tard, sans alourdir le document.
 */

export interface EditorChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface EditorChatSession {
  id: string;
  title: string;
  mode: string;
  createdAt: number;
  updatedAt: number;
  messages: EditorChatMessage[];
}

const MAX_SESSIONS = 25;
const MAX_MESSAGES = 80;

function storageKey(historyKey: string): string {
  return `zifek:editor-chats:${historyKey}`;
}

function safeParse(raw: string | null): EditorChatSession[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is EditorChatSession => !!s && typeof s.id === 'string' && Array.isArray(s.messages));
  } catch {
    return [];
  }
}

export function listChatSessions(historyKey: string): EditorChatSession[] {
  if (!historyKey || typeof localStorage === 'undefined') return [];
  return safeParse(localStorage.getItem(storageKey(historyKey))).sort((a, b) => b.updatedAt - a.updatedAt);
}

export function saveChatSession(
  historyKey: string,
  session: EditorChatSession,
): EditorChatSession[] {
  if (!historyKey || typeof localStorage === 'undefined') return [];
  const sessions = listChatSessions(historyKey).filter((s) => s.id !== session.id);
  const next = [{ ...session, messages: session.messages.slice(-MAX_MESSAGES) }, ...sessions].slice(0, MAX_SESSIONS);
  try {
    localStorage.setItem(storageKey(historyKey), JSON.stringify(next));
  } catch {
    /* quota navigateur atteint : l'historique est simplement ignoré */
  }
  return next;
}

export function deleteChatSession(historyKey: string, id: string): EditorChatSession[] {
  if (!historyKey || typeof localStorage === 'undefined') return [];
  const next = listChatSessions(historyKey).filter((s) => s.id !== id);
  try {
    localStorage.setItem(storageKey(historyKey), JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

export function clearChatSessions(historyKey: string): void {
  if (!historyKey || typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(storageKey(historyKey));
  } catch {
    /* ignore */
  }
}

export function makeSessionId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `chat-${Date.now()}`;
}

/** Titre court tiré du premier message utilisateur. */
export function sessionTitle(messages: EditorChatMessage[]): string {
  const first = messages.find((m) => m.role === 'user')?.content || 'Conversation';
  const clean = first.replace(/\s+/g, ' ').trim();
  return clean.length > 52 ? `${clean.slice(0, 52)}…` : clean;
}

export function relativeSessionDate(ts: number): string {
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}